import type { ActionReturn } from "svelte/action";

interface inViewParams {
  root?: HTMLElement;
  top?: number;
  bottom?: number;
}

interface inViewAttributes {
  "on:enter"?: (e: CustomEvent<boolean>) => void;
  "on:exit"?: (e: CustomEvent<boolean>) => void;
}

/**
 * Based on https://github.com/the-pudding/svelte-starter/blob/svelte/src/actions/inView.js
 *
 * This action triggers a custom event on node entering/exiting the viewport.
 * example:
 * <p
 * 	use:inView
 * 	on:enter={() => console.log("enter")}
 * 	on:exit={() => console.log("exit")}
 * >
 *
 * optional params { root, top, bottom }
 * top and bottom are numbers
 * use:inView={ bottom: 100 } // 100 pixels from bottom of viewport
 */

export function inView(
  node: HTMLElement,
  params: inViewParams = {},
): ActionReturn<inViewParams, inViewAttributes> {
  let observer: IntersectionObserver | null = null;

  const handleIntersect: IntersectionObserverCallback = (e) => {
    const v = e[0].isIntersecting ? "enter" : "exit";
    node.dispatchEvent(new CustomEvent(v));
  };

  const setObserver = ({ root, top, bottom }: inViewParams) => {
    const marginTop = top ? top * -1 : 0;
    const marginBottom = bottom ? bottom * -1 : 0;
    const rootMargin = `${marginTop}px 0px ${marginBottom}px 0px`;
    const options = { root, rootMargin };
    if (observer) observer.disconnect();
    observer = new IntersectionObserver(handleIntersect, options);
    observer.observe(node);
  };

  setObserver(params);

  return {
    update(params) {
      setObserver(params);
    },

    destroy() {
      if (observer) observer.disconnect();
    },
  };
}
